import React from 'react';
import { useNavigate } from 'react-router-dom';
import MainLayout from '@/components/layouts/MainLayout';
import PageHeader from '@/components/common/PageHeader';
import DashboardCard from '@/components/common/DashboardCard';
import { CreditCard, DollarSign, FileText, HelpCircle } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useAuthor } from '@/hooks/useAuthors';
import { useRoyaltyReports } from '@/hooks/useRoyaltyReports';
import { usePaymentRequests } from '@/hooks/usePaymentRequests';
import { useSupportTickets } from '@/hooks/useSupportTickets';
import PaymentEligibilityCard from '@/components/dashboard/PaymentEligibilityCard';
import RoyaltyEarningsChart from '@/components/dashboard/RoyaltyEarningsChart';
import RecentRoyaltyReports from '@/components/dashboard/RecentRoyaltyReports';
import HelpSection from '@/components/dashboard/HelpSection';
import { useIdleTimeout } from '@/hooks/useIdleTimeout';

const AuthorDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const userId = user?.id || '';
  
  // Enable idle timeout for automatic logout
  useIdleTimeout();
  
  // Fetch data
  const { data: author } = useAuthor(userId);
  const { data: royaltyReports } = useRoyaltyReports(userId);
  const { data: paymentRequests } = usePaymentRequests(userId);
  const { data: tickets } = useSupportTickets(userId);
  
  // Prepare chart data
  const royaltyChartData = React.useMemo(() => {
    if (!royaltyReports) return [];
    
    const quarters = {} as Record<string, number>;
    
    royaltyReports.forEach(report => {
      const key = `Q${report.quarter} ${report.year}`;
      if (!quarters[key]) quarters[key] = 0;
      quarters[key] += parseFloat(String(report.amount));
    });
    
    return Object.entries(quarters)
      .map(([name, amount]) => ({ name, amount }))
      .sort((a, b) => {
        const [aQ, aY] = a.name.split(' ');
        const [bQ, bY] = b.name.split(' ');
        
        if (aY !== bY) return parseInt(aY) - parseInt(bY);
        return parseInt(aQ.substring(1)) - parseInt(bQ.substring(1));
      })
      .slice(-4);  // Get the last 4 quarters
  }, [royaltyReports]);
  
  // Get payment details
  const paymentMethod = author?.paymentMethod?.type;
  const threshold = author?.paymentMethod?.threshold || 0;
  const balance = author?.balance || 0;
  const canRequestPayment = balance >= threshold;
  
  // Get recent royalties
  const recentRoyalties = royaltyReports?.slice(0, 5) || [];
  
  // Count pending payments and active tickets
  const pendingPaymentsCount = paymentRequests?.filter(
    p => p.status === 'pending' || p.status === 'approved'
  ).length || 0;
  
  const activeTicketsCount = tickets?.filter(
    t => t.status === 'open' || t.status === 'in_progress'
  ).length || 0;
  
  return (
    <MainLayout allowedRoles={['author']}>
      <PageHeader 
        title={`Welcome, ${author?.name || 'Author'}`} 
        description="Your royalty and payment dashboard"
      />
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <DashboardCard
          title="Current Balance"
          value={`$${balance.toFixed(2)}`}
          icon={<DollarSign className="h-4 w-4" />}
          description="Available for withdrawal"
          trend="up"
          trendValue="8% from last quarter"
        />
        <DashboardCard
          title="Books Published"
          value={author?.books?.length || 0}
          icon={<FileText className="h-4 w-4" />}
          description="Titles earning royalties"
          onClick={() => navigate('/author/books')}
        />
        <DashboardCard
          title="Pending Payments"
          value={pendingPaymentsCount}
          icon={<CreditCard className="h-4 w-4" />}
          description="Payment requests in process"
          onClick={() => navigate('/author/payments')}
        />
        <DashboardCard
          title="Active Tickets"
          value={activeTicketsCount}
          icon={<HelpCircle className="h-4 w-4" />}
          description="Support tickets you've opened"
          onClick={() => navigate('/author/help')}
        />
      </div>

      {author && (
        <PaymentEligibilityCard
          balance={balance}
          paymentMethod={paymentMethod}
          threshold={threshold}
          canRequestPayment={canRequestPayment}
        />
      )}
      
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <RoyaltyEarningsChart data={royaltyChartData} />
        <RecentRoyaltyReports 
          reports={recentRoyalties} 
          onViewAll={() => navigate('/author/royalties')} 
        />
      </div>

      <HelpSection />
    </MainLayout>
  );
};

export default AuthorDashboard;
